import {
  Badge,
  Box,
  Button,
  Center,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import type { ShiptoDepositCrud } from '@diamond/shared/schema/shipto-deposit';
import { Pagination, Text } from '@diamond/shared/ui';
import { buyerCurrencyFormatter } from '@diamond/shared/utils';
import {
  type OrderHistoryPoLookupRow,
  formatNlbTransactionAt,
} from '@diamond/sol-buyer/utils';

import { NlbAmountCell } from './NlbAmountCell';
import { NlbTransactionDetailCell } from './NlbTransactionDetailCell';
import { PaymentInfoNotes } from './PaymentInfoNotes';

type HistoryResponse = ShiptoDepositCrud['getTransactionHistories']['response'];

export interface NlbTransactionHistoryTableProps {
  data: HistoryResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  page: number;
  onPageChange: (page: number) => void;
  orders: OrderHistoryPoLookupRow[] | undefined;
  warehouseId: string;
  onRetry?: () => void;
}

function movementTypeLabel(
  movementType: HistoryResponse['data'][number]['movement_type']
) {
  return movementType === 'credit_shipto' ? 'Kredit' : 'Debit';
}

function EmptyHistoryMessage() {
  return (
    <Center py={8}>
      <Text fontSize="sm" color="gray.600">
        Belum ada transaksi terkait Nominal Lebih Bayar.
      </Text>
    </Center>
  );
}

export function NlbTransactionHistoryTable({
  data,
  isLoading,
  isError,
  page,
  onPageChange,
  orders,
  warehouseId,
  onRetry,
}: NlbTransactionHistoryTableProps) {
  const rows = data?.data ?? [];
  const totalPages = data?.pagination.pages ?? 0;

  return (
    <Stack spacing={4}>
      <Text
        fontSize={{ base: '20px', md: 'lg' }}
        fontWeight={{ base: 'medium', md: 'semibold' }}
        mb={2}
        whiteSpace="normal"
      >
        Riwayat Nominal Lebih Bayar
      </Text>
      <Box mb={4}>
        <PaymentInfoNotes />
      </Box>

      {isError ? (
        <Center py={8} flexDirection="column" gap={3}>
          <Text fontSize="sm" color="gray.600">
            Gagal memuat riwayat Nominal Lebih Bayar.
          </Text>
          {onRetry ? (
            <Button variant="link" color="blue" fontSize="sm" onClick={onRetry}>
              Coba lagi
            </Button>
          ) : null}
        </Center>
      ) : isLoading ? (
        <Center py={12}>
          <Spinner color="blue" />
        </Center>
      ) : (
        <>
          <TableContainer
            border="1px solid"
            borderColor="gray.33"
            borderRadius="lg"
            overflowX={{ base: 'visible', md: 'auto' }}
          >
            <Table
              variant="simple"
              size="sm"
              minW={{ base: 'auto', md: '720px' }}
            >
              <Thead bg="gray.50">
                <Tr>
                  <Th>
                    <Box as="span" display={{ base: 'inline', md: 'none' }}>
                      Waktu Transaksi
                    </Box>
                    <Box as="span" display={{ base: 'none', md: 'inline' }}>
                      Waktu
                    </Box>
                  </Th>
                  <Th>Rincian Transaksi</Th>
                  <Th display={{ base: 'none', md: 'table-cell' }} isNumeric>
                    Nilai Transfer (Rp)
                  </Th>
                  <Th display={{ base: 'none', md: 'table-cell' }} isNumeric>
                    Faktur Akhir
                  </Th>
                  <Th isNumeric>Lebih Bayar (Rp)</Th>
                  <Th display={{ base: 'none', md: 'table-cell' }}>Tipe</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.length === 0 ? (
                  <>
                    <Tr display={{ base: 'table-row', md: 'none' }}>
                      <Td colSpan={3}>
                        <EmptyHistoryMessage />
                      </Td>
                    </Tr>
                    <Tr display={{ base: 'none', md: 'table-row' }}>
                      <Td colSpan={6}>
                        <EmptyHistoryMessage />
                      </Td>
                    </Tr>
                  </>
                ) : (
                  rows.map((row) => (
                    <Tr key={row.id}>
                      <Td whiteSpace="nowrap">
                        {formatNlbTransactionAt(row.transaction_at)}
                      </Td>
                      <Td>
                        <NlbTransactionDetailCell
                          row={row}
                          orders={orders}
                          warehouseId={warehouseId}
                        />
                      </Td>
                      <Td
                        display={{ base: 'none', md: 'table-cell' }}
                        isNumeric
                      >
                        {buyerCurrencyFormatter(row.transfer_amount)}
                      </Td>
                      <Td
                        display={{ base: 'none', md: 'table-cell' }}
                        isNumeric
                      >
                        {buyerCurrencyFormatter(row.final_billing_amount)}
                      </Td>
                      <Td isNumeric>
                        <NlbAmountCell
                          movementType={row.movement_type}
                          amount={row.amount}
                        />
                      </Td>
                      <Td display={{ base: 'none', md: 'table-cell' }}>
                        <Badge
                          colorScheme={
                            row.movement_type === 'credit_shipto'
                              ? 'blue'
                              : 'red'
                          }
                        >
                          {movementTypeLabel(row.movement_type)}
                        </Badge>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>

          {totalPages > 0 ? (
            <Box mt={4}>
              <Pagination
                currentPage={page}
                totalPage={totalPages}
                previousPage={() => onPageChange(Math.max(1, page - 1))}
                nextPage={() => onPageChange(Math.min(totalPages, page + 1))}
                firstPage={() => onPageChange(1)}
                lastPage={() => onPageChange(totalPages)}
              />
            </Box>
          ) : null}
        </>
      )}
    </Stack>
  );
}
